/**
 * Formulaire officiel de diagnostic d’activité — IGPE
 * Source : FORMULAIRE DE DIAGNOSTIC ACTIVITE.pdf
 *
 * Scoring : Oui = 4 · Non = 1 · Partiel = 2
 * IGPE (%) = (somme des résultats pondérés / 2452) × 100
 * Vulnérabilité : dimension dont le résultat < 50 % du total cible
 */

export type DiagnosticAnswerValue = 'oui' | 'non' | 'partiel';

export const DIAGNOSTIC_ANSWER_POINTS: Record<DiagnosticAnswerValue, number> = {
  oui: 4,
  non: 1,
  partiel: 2,
};

export const DIAGNOSTIC_ANSWER_OPTIONS: {
  value: DiagnosticAnswerValue;
  label: string;
}[] = [
  { value: 'oui', label: 'Oui' },
  { value: 'non', label: 'Non' },
  { value: 'partiel', label: 'Partiel' },
];

export interface DiagnosticQuestion {
  id: string;
  label: string;
}

export interface DiagnosticSection {
  key: string;
  letter: string;
  label: string;
  noteCible: number;
  coefficient: number;
  totalCible: number;
  questions: DiagnosticQuestion[];
}

export const DIAGNOSTIC_SECTIONS: DiagnosticSection[] = [
  {
    key: 'leadership',
    letter: 'A',
    label: 'Leadership entrepreneurial',
    noteCible: 20,
    coefficient: 10,
    totalCible: 200,
    questions: [
      { id: 'leadership_1', label: "J'ai une vision claire de l'avenir de mon entreprise." },
      { id: 'leadership_2', label: 'Je connais les objectifs de mon entreprise à 1, 3 et 5 ans.' },
      {
        id: 'leadership_3',
        label: "Je prends des décisions basées sur des données plutôt que sur l'intuition.",
      },
      { id: 'leadership_4', label: 'Je sais mobiliser mon équipe autour de mes objectifs.' },
      { id: 'leadership_5', label: 'Je gère efficacement les changements.' },
    ],
  },
  {
    key: 'strategie',
    letter: 'B',
    label: 'Stratégie',
    noteCible: 20,
    coefficient: 10,
    totalCible: 200,
    questions: [
      { id: 'strategie_1', label: "Mon entreprise dispose d'un plan stratégique écrit." },
      { id: 'strategie_2', label: 'Les objectifs sont suivis régulièrement.' },
      { id: 'strategie_3', label: 'Je connais mes principaux concurrents.' },
      { id: 'strategie_4', label: 'Je réalise régulièrement une analyse du marché.' },
      {
        id: 'strategie_5',
        label: "J'adapte rapidement ma stratégie lorsque le marché évolue.",
      },
    ],
  },
  {
    key: 'modele_economique',
    letter: 'C',
    label: 'Modèle économique',
    noteCible: 20,
    coefficient: 10,
    totalCible: 200,
    questions: [
      { id: 'modele_1', label: 'Mon modèle économique est clairement défini.' },
      { id: 'modele_2', label: 'Je connais mes principales sources de revenus.' },
      { id: 'modele_3', label: 'Mes coûts sont bien maîtrisés.' },
      { id: 'modele_4', label: 'Mon activité est rentable.' },
      {
        id: 'modele_5',
        label: "J'identifie régulièrement de nouvelles opportunités de revenus.",
      },
    ],
  },
  {
    key: 'marketing_ventes',
    letter: 'D',
    label: 'Marketing et ventes',
    noteCible: 32,
    coefficient: 17,
    totalCible: 544,
    questions: [
      {
        id: 'marketing_1',
        label: 'Mon entreprise possède une identité visuelle professionnelle.',
      },
      {
        id: 'marketing_2',
        label: "Je dispose d'un site internet ou d'une présence numérique.",
      },
      { id: 'marketing_3', label: "J'utilise efficacement les réseaux sociaux." },
      { id: 'marketing_4', label: 'Je connais précisément mon client cible.' },
      {
        id: 'marketing_5',
        label: 'Mon entreprise attire régulièrement de nouveaux prospects.',
      },
      { id: 'marketing_6', label: 'Mon taux de conversion est satisfaisant.' },
      { id: 'marketing_7', label: 'Mes clients reviennent acheter.' },
      { id: 'marketing_8', label: 'Je mesure la satisfaction de mes clients.' },
    ],
  },
  {
    key: 'gestion_financiere',
    letter: 'E',
    label: 'Gestion financière',
    noteCible: 32,
    coefficient: 15,
    totalCible: 480,
    questions: [
      { id: 'finance_1', label: 'Ma comptabilité est tenue à jour.' },
      { id: 'finance_2', label: "Je dispose d'un budget annuel." },
      { id: 'finance_3', label: 'Je connais ma trésorerie.' },
      { id: 'finance_4', label: 'Je connais mes marges.' },
      { id: 'finance_5', label: 'Je maîtrise mes coûts.' },
      {
        id: 'finance_6',
        label: 'Je prépare régulièrement des prévisions financières.',
      },
      { id: 'finance_7', label: 'Je peux financer mon activité sans difficulté.' },
      { id: 'finance_8', label: 'Mes impayés sont maîtrisés.' },
    ],
  },
  {
    key: 'relation_client',
    letter: 'F',
    label: 'Relations clients',
    noteCible: 20,
    coefficient: 10,
    totalCible: 200,
    questions: [
      { id: 'client_1', label: 'Les informations de mes clients sont centralisées.' },
      { id: 'client_2', label: 'Je communique régulièrement avec eux.' },
      { id: 'client_3', label: 'Je traite rapidement les réclamations.' },
      { id: 'client_4', label: 'Je mesure leur satisfaction.' },
      { id: 'client_5', label: 'Je mets en œuvre des actions de fidélisation.' },
    ],
  },
  {
    key: 'operations',
    letter: 'G',
    label: 'Opérations et organisation',
    noteCible: 28,
    coefficient: 10,
    totalCible: 280,
    questions: [
      { id: 'ops_1', label: 'Les procédures sont documentées.' },
      { id: 'ops_2', label: 'Les responsabilités sont clairement définies.' },
      { id: 'ops_3', label: 'Les délais sont respectés.' },
      { id: 'ops_4', label: 'Les erreurs sont rares.' },
      { id: 'ops_5', label: 'La qualité est contrôlée.' },
      { id: 'ops_6', label: 'Les stocks sont bien gérés.' },
      { id: 'ops_7', label: 'La productivité est suivie.' },
    ],
  },
  {
    key: 'ressources_humaines',
    letter: 'H',
    label: 'Ressources humaines',
    noteCible: 20,
    coefficient: 5,
    totalCible: 100,
    questions: [
      { id: 'rh_1', label: 'Les postes sont clairement définis.' },
      { id: 'rh_2', label: 'Les employés sont compétents.' },
      { id: 'rh_3', label: 'Des formations sont organisées.' },
      { id: 'rh_4', label: 'Les performances du personnel sont évaluées.' },
      { id: 'rh_5', label: 'Le climat social est satisfaisant.' },
    ],
  },
  {
    key: 'digitalisation',
    letter: 'I',
    label: 'Digitalisation',
    noteCible: 20,
    coefficient: 5,
    totalCible: 100,
    questions: [
      { id: 'digital_1', label: "J'utilise un logiciel de gestion." },
      { id: 'digital_2', label: 'Les documents sont numérisés.' },
      {
        id: 'digital_3',
        label: 'Les paiements peuvent être réalisés électroniquement.',
      },
      { id: 'digital_4', label: "J'utilise des outils collaboratifs." },
      {
        id: 'digital_5',
        label: "J'utilise l'intelligence artificielle ou des outils d'automatisation.",
      },
    ],
  },
  {
    key: 'innovation',
    letter: 'J',
    label: 'Innovation',
    noteCible: 20,
    coefficient: 5,
    totalCible: 100,
    questions: [
      {
        id: 'innovation_1',
        label: 'Je développe régulièrement de nouveaux produits ou services.',
      },
      { id: 'innovation_2', label: "J'investis dans l'amélioration de mon entreprise." },
      { id: 'innovation_3', label: 'Je pratique une veille concurrentielle.' },
      { id: 'innovation_4', label: "J'encourage les nouvelles idées." },
      {
        id: 'innovation_5',
        label: "J'expérimente régulièrement de nouvelles approches.",
      },
    ],
  },
  {
    key: 'gouvernance',
    letter: 'K',
    label: 'Gouvernance et conformité',
    noteCible: 16,
    coefficient: 3,
    totalCible: 48,
    questions: [
      { id: 'gouvernance_1', label: 'Les documents juridiques sont à jour.' },
      { id: 'gouvernance_2', label: 'Les obligations fiscales sont respectées.' },
      { id: 'gouvernance_3', label: 'Les obligations sociales sont respectées.' },
      { id: 'gouvernance_4', label: 'Les risques sont identifiés.' },
    ],
  },
];

export const IGPE_TOTAL_CIBLE = DIAGNOSTIC_SECTIONS.reduce((s, d) => s + d.totalCible, 0); // 2452

export type IgpeLevel =
  | 'excellent'
  | 'tres_performant'
  | 'performant'
  | 'en_developpement'
  | 'fragile'
  | 'critique'
  | 'tres_critique';

export const IGPE_LEVEL_BANDS: {
  min: number;
  max: number;
  level: IgpeLevel;
  label: string;
  recommendation: string;
}[] = [
  {
    min: 90,
    max: 100,
    level: 'excellent',
    label: 'Excellent',
    recommendation:
      "Développer l'expansion, l'innovation et l'internationalisation. Par l'accompagnement stratégique qu'offre ISBD, vous pourrez atteindre cet objectif.",
  },
  {
    min: 80,
    max: 89,
    level: 'tres_performant',
    label: 'Très performant',
    recommendation:
      "Consolider les acquis et accélérer la croissance. Par l'accompagnement stratégique qu'offre ISBD, vous pourrez atteindre cet objectif.",
  },
  {
    min: 70,
    max: 79,
    level: 'performant',
    label: 'Performant',
    recommendation:
      "Optimiser les domaines présentant quelques faiblesses. Par l'accompagnement stratégique qu'offre ISBD, vous pourrez atteindre cet objectif.",
  },
  {
    min: 60,
    max: 69,
    level: 'en_developpement',
    label: 'En développement',
    recommendation: "Mettre en œuvre un plan d'amélioration ciblé.",
  },
  {
    min: 50,
    max: 59,
    level: 'fragile',
    label: 'Fragile',
    recommendation:
      "Renforcer les fondamentaux avec un accompagnement structuré. Par l'accompagnement stratégique qu'offre ISBD, vous pourrez atteindre cet objectif.",
  },
  {
    min: 40,
    max: 49,
    level: 'critique',
    label: 'Critique',
    recommendation:
      "Élaborer un plan de redressement prioritaire. Par l'accompagnement stratégique qu'offre ISBD, vous pourrez atteindre cet objectif.",
  },
  {
    min: 0,
    max: 39,
    level: 'tres_critique',
    label: 'Très critique',
    recommendation:
      "Accompagnement intensif, restructuration et suivi rapproché. Par l'accompagnement stratégique qu'offre ISBD, vous pourrez atteindre cet objectif.",
  },
];

export function igpeLevelFromScore(score: number): IgpeLevel {
  const s = Math.max(0, Math.min(100, Math.round(score)));
  const band = IGPE_LEVEL_BANDS.find((b) => s >= b.min && s <= b.max);
  return band?.level ?? 'tres_critique';
}

export function igpeLevelMeta(level: IgpeLevel) {
  return IGPE_LEVEL_BANDS.find((b) => b.level === level)!;
}

export function igpeLevelLabel(level: IgpeLevel): string {
  return igpeLevelMeta(level).label;
}

export function igpeRecommendation(level: IgpeLevel): string {
  return igpeLevelMeta(level).recommendation;
}

export type DiagnosticAnswers = Record<string, DiagnosticAnswerValue | ''>;
export type DiagnosticObservations = Record<string, string>;

export function emptyDiagnosticAnswers(): DiagnosticAnswers {
  const out: DiagnosticAnswers = {};
  for (const section of DIAGNOSTIC_SECTIONS) {
    for (const q of section.questions) out[q.id] = '';
  }
  return out;
}

export function emptyDiagnosticObservations(): DiagnosticObservations {
  const out: DiagnosticObservations = {};
  for (const section of DIAGNOSTIC_SECTIONS) {
    for (const q of section.questions) out[q.id] = '';
  }
  return out;
}

export function diagnosticFormComplete(answers: DiagnosticAnswers): boolean {
  return DIAGNOSTIC_SECTIONS.every((section) =>
    section.questions.every((q) => answers[q.id] === 'oui' || answers[q.id] === 'non' || answers[q.id] === 'partiel'),
  );
}

export interface IgpeDimensionScore {
  key: string;
  label: string;
  note_cible: number;
  coefficient: number;
  total_cible: number;
  note_obtenue: number;
  resultat: number;
  pct_cible: number;
  is_vulnerable: boolean;
  comment: string;
  /** @deprecated alias note_obtenue pour compat affichage */
  score: number;
}

export interface IgpeVulnerability {
  dimension: string;
  title: string;
  severity: 'faible' | 'moyenne' | 'elevee';
  detail: string;
  recommendation: string;
  resultat: number;
  total_cible: number;
  pct_cible: number;
}

export interface DiagnosticIgpeResult {
  igpe_score: number;
  igpe_level: IgpeLevel;
  total_obtenu: number;
  total_cible: number;
  dimensions: IgpeDimensionScore[];
  vulnerabilities: IgpeVulnerability[];
  summary: string;
  recommendation: string;
  /** Format CDC */
  report_results: string;
  report_strengths: string[];
  report_weaknesses: string[];
  report_recommendations: string[];
}

export interface DiagnosticReport {
  id: string;
  user_id: string;
  ideation_id: string;
  context_answers: Record<string, unknown>;
  igpe_score: number;
  igpe_level: IgpeLevel;
  dimensions: IgpeDimensionScore[];
  vulnerabilities: IgpeVulnerability[];
  summary: string;
  report_results?: string;
  report_strengths?: string;
  report_weaknesses?: string;
  report_recommendations?: string;
  ai_raw: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export function computeDiagnosticIgpe(
  answers: DiagnosticAnswers,
  observations: DiagnosticObservations = {},
): DiagnosticIgpeResult {
  const dimensions: IgpeDimensionScore[] = [];
  let totalObtenu = 0;

  for (const section of DIAGNOSTIC_SECTIONS) {
    let noteObtenue = 0;
    const obsBits: string[] = [];
    for (const q of section.questions) {
      const ans = answers[q.id];
      if (ans === 'oui' || ans === 'non' || ans === 'partiel') {
        noteObtenue += DIAGNOSTIC_ANSWER_POINTS[ans];
      }
      const obs = (observations[q.id] ?? '').trim();
      if (obs) obsBits.push(`${q.label} — ${obs}`);
    }
    const resultat = noteObtenue * section.coefficient;
    const pct = section.totalCible > 0 ? (resultat / section.totalCible) * 100 : 0;
    const isVulnerable = resultat < section.totalCible * 0.5;
    totalObtenu += resultat;
    dimensions.push({
      key: section.key,
      label: section.label,
      note_cible: section.noteCible,
      coefficient: section.coefficient,
      total_cible: section.totalCible,
      note_obtenue: noteObtenue,
      resultat,
      pct_cible: Math.round(pct * 10) / 10,
      is_vulnerable: isVulnerable,
      comment: obsBits.slice(0, 2).join(' · '),
      score: noteObtenue,
    });
  }

  const igpeScore = Math.round((totalObtenu / IGPE_TOTAL_CIBLE) * 1000) / 10;
  const level = igpeLevelFromScore(igpeScore);
  const recommendation = igpeRecommendation(level);

  const vulnerabilities: IgpeVulnerability[] = dimensions
    .filter((d) => d.is_vulnerable)
    .map((d) => ({
      dimension: d.label,
      title: d.label,
      severity: d.pct_cible < 30 ? 'elevee' : d.pct_cible < 40 ? 'moyenne' : 'faible',
      detail: `Résultat ${d.resultat} / ${d.total_cible} (${d.pct_cible.toFixed(0)} % du total cible).`,
      recommendation: `Prioriser un plan d'amélioration sur « ${d.label} ».`,
      resultat: d.resultat,
      total_cible: d.total_cible,
      pct_cible: d.pct_cible,
    }));

  const summary = `Votre Indice Global de Performance Entrepreneuriale est de ${igpeScore} % (${igpeLevelLabel(level)}).`;

  const byPctDesc = [...dimensions].sort((a, b) => b.pct_cible - a.pct_cible);
  const byPctAsc = [...dimensions].sort((a, b) => a.pct_cible - b.pct_cible);
  const report_strengths = byPctDesc
    .filter((d) => d.pct_cible >= 50)
    .slice(0, 4)
    .map((d) => `${d.label} : ${d.pct_cible.toFixed(0)} % du total cible`);
  if (!report_strengths.length && byPctDesc[0]) {
    report_strengths.push(`${byPctDesc[0].label} est la dimension la plus solide.`);
  }
  const report_weaknesses = byPctAsc
    .filter((d) => d.is_vulnerable)
    .slice(0, 4)
    .map((d) => `${d.label} : ${d.pct_cible.toFixed(0)} % du total cible — ${d.comment || 'axe prioritaire'}`);
  if (!report_weaknesses.length) {
    report_weaknesses.push('Aucune dimension sous le seuil de vulnérabilité (50 % du total cible).');
  }
  const report_recommendations = [
    recommendation,
    ...vulnerabilities.slice(0, 3).map((v) => v.recommendation),
  ];
  const report_results =
    `${summary} Score pondéré ${totalObtenu}/${IGPE_TOTAL_CIBLE}. ` +
    `Niveau : ${igpeLevelLabel(level)}.`;

  return {
    igpe_score: igpeScore,
    igpe_level: level,
    total_obtenu: totalObtenu,
    total_cible: IGPE_TOTAL_CIBLE,
    dimensions,
    vulnerabilities,
    summary,
    recommendation,
    report_results,
    report_strengths,
    report_weaknesses,
    report_recommendations,
  };
}

export const IDEATION_ENTRY_KINDS = [
  {
    key: 'assisted' as const,
    title: 'Je n’ai pas d’idée',
    description:
      'Indiquez votre domaine, zone et capital possible ; l’IA propose au moins 3 idées porteuses.',
    nextLabel: 'Faisabilité',
    requiresPpco: true,
    projectLevel: 'nouveau' as const,
  },
  {
    key: 'new_idea' as const,
    title: 'J’ai une idée, pas encore démarrée',
    description: 'Enregistrez votre idée (statut non validé), validez-la via PPCO, puis retenez-la comme projet.',
    nextLabel: 'Faisabilité',
    requiresPpco: true,
    projectLevel: 'nouveau' as const,
  },
  {
    key: 'existing_activity' as const,
    title: 'Activité déjà en exécution',
    description: 'Enregistrez votre activité comme projet « en cours » et passez directement au diagnostic.',
    nextLabel: 'Diagnostic',
    requiresPpco: false,
    projectLevel: 'en_cours' as const,
  },
] as const;

export type IdeationEntryKind = (typeof IDEATION_ENTRY_KINDS)[number]['key'];

export function entryKindMeta(kind: IdeationEntryKind) {
  return IDEATION_ENTRY_KINDS.find((k) => k.key === kind)!;
}

/** Libellés métier CDC pour les statuts d’idéation. */
export function ideationStatusLabel(status: string): string {
  switch (status) {
    case 'generated':
    case 'ppco_done':
      return 'Non validé';
    case 'retained':
      return 'Retenu (projet)';
    case 'rejected':
      return 'Rejeté';
    case 'project_linked':
      return 'Projet';
    default:
      return status;
  }
}

export function projectLevelLabel(level: string): string {
  return level === 'en_cours' ? 'En cours' : 'Nouveau';
}

export function viabilityLabel(v: string): string {
  if (v === 'viable') return 'Viable';
  if (v === 'not_viable') return 'Non viable';
  if (v === 'uncertain') return 'Incertain';
  return 'En attente';
}
